import React, { useState, useCallback, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Box, Typography, Stack, Paper, CircularProgress, Grid, Checkbox,
  FormControlLabel, List, ListItem, ListItemText, Autocomplete, TextField, Alert,
  ToggleButton, ToggleButtonGroup, Chip, Divider, InputAdornment, Tooltip
} from '@mui/material';
import {
  Close as CloseIcon, PhotoLibrary as PhotoLibraryIcon, PictureAsPdf as PictureAsPdfIcon,
  CloudUpload as CloudUploadIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon,
  ImageSearch as ImageSearchIcon, Fullscreen as FullscreenIcon, AddCircleOutline as AddCircleOutlineIcon,
  VideoLibraryOutlined, TextFields as TextFieldsIcon, Settings as SettingsIcon,
  Print as PrintIcon, Restaurant as RestaurantIcon, ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import { contentSetApi, mediaApi } from '../apiUser';
import { InfoContext } from '../context/InfoContext';
import WaitingDialog from './WaitingDialog';

// --- Sottocomponente Dropzone (Invariato nella logica, ottimizzato nello stile) ---
function Dropzone({ onDrop, accept, isPdf = false, inputRef, maxFiles = 0 }) { // maxFiles 0 = unlimited
    const [isDragging, setIsDragging] = useState(false);
    const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        onDrop(e.dataTransfer.files);
    };
    const onButtonClick = () => { inputRef.current.click(); };

    return (
        <Paper
            variant="outlined"
            onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
            onClick={onButtonClick}
            sx={{
                border: '2px dashed', borderColor: isDragging ? 'primary.main' : 'grey.400',
                backgroundColor: isDragging ? 'action.hover' : 'grey.50', p: 4, textAlign: 'center', cursor: 'pointer',
                transition: 'all 0.2s ease-in-out', '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' }
            }}
        >
            <input ref={inputRef} type="file" accept={accept} multiple={maxFiles !== 1} style={{ display: 'none' }} onChange={(e) => onDrop(e.target.files)} />
            <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? 'primary.main' : 'grey.500', mb: 2 }} />
            <Typography variant="h6">{isDragging ? "Rilascia ora" : "Clicca o trascina qui"}</Typography>
            <Typography variant="body2" color="text.secondary">
                {isPdf ? "Solo file PDF." : maxFiles === 1 ? "Carica un singolo file." : "Carica uno o più file."}
            </Typography>
        </Paper>
    );
}

// --- Sottocomponente per Input Multipli (Banchi, Articoli) ---
function TagInput({ label, placeholder, value, onChange, maxLength }) {
  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={value}
      onChange={(event, newValue) => {
        // Filtra valori troppo lunghi se c'è un limite
        if (maxLength) {
           const validValues = newValue.filter(v => v.length <= maxLength);
           if(validValues.length !== newValue.length) {
              // Opzionale: notifica errore lunghezza
           }
           onChange(validValues);
        } else {
           onChange(newValue);
        }
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip variant="outlined" label={option} {...getTagProps({ index })} color="primary" />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={value.length > 0 ? "" : placeholder}
          helperText={maxLength ? `Premi Invio per aggiungere. Max ${maxLength} caratteri.` : "Premi Invio per aggiungere."}
        />
      )}
    />
  );
}

// --- Componente Principale ---
export default function Uploader({ open, onClose, onSave, mediaMode = 'totem' }) { // mediaMode: 'monitor' | 'totem'

  const { info } = useContext(InfoContext);
  const userInfos = info.selectedClient;
  const clientId = userInfos?.client_id;

  // Stati Generali
  const [step, setStep] = useState('contentSet');
  const [contentSets, setContentSets] = useState([]);
  const [selectedContentSet, setSelectedContentSet] = useState(null);
  const [showNewContentSet, setShowNewContentSet] = useState(false);
  const [newContentSetName, setNewContentSetName] = useState("");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState(null);
  
  // Stati PDF/Image (Monitor)
  const [pdfFile, setPdfFile] = useState(null);
  const [extractedImages, setExtractedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(true);

  // Stati Totem
  const [totemType, setTotemType] = useState(null); // 'image' | 'text'
  const [totemText, setTotemText] = useState("");
  const [totemConfig, setTotemConfig] = useState({
    trigger: 1, // prenotazione | ingredienti
    counters: [],
    articles: [],
    priority: 5,
    queueThreshold: 0
  });

  const inputRef = React.useRef(null);

  const getContentSets = async () => {
    if (!clientId) return;
    setIsLoading(true);
    setLoadingMessage("Caricamento content set...");
    try {
      const response = await contentSetApi.get(clientId);
      setContentSets(response.data || []);
    } catch (err) {
      console.error("Errore recupero content set:", err);
      setError("Impossibile caricare i content set.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getContentSets();
      resetState();
    }
  }, [open, clientId]);

  const resetState = () => {
    setStep('contentSet');
    setFiles([]);
    setPdfFile(null);
    setExtractedImages([]);
    setSelectedImages([]);
    setFullscreenImage(null);
    setSelectedContentSet(null);
    setShowNewContentSet(false);
    setNewContentSetName("");
    setError(null);
    
    // Reset Totem
    setTotemType(null);
    setTotemText("");
    setTotemConfig({
      trigger: 1, // prenotazione | ingredienti
      counters: [],
      articles: [],
      priority: 5,
      queueThreshold: 0
    });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // --- Handlers Generici ---

  const handleCreateContentSet = async () => {
    if (!newContentSetName.trim()) return;
    setIsLoading(true);
    setLoadingMessage("Creazione nuovo set...");
    try {
      const response = await contentSetApi.create({ content_set_name: newContentSetName, content_set_description: newContentSetName });
      const newSet = response.data;
      setContentSets(prev => [...prev, newSet]);
      setSelectedContentSet(newSet);
      setShowNewContentSet(false);
      setNewContentSetName("");
    } catch (err) {
      console.error("Errore creazione set:", err);
      setError("Errore durante la creazione del set.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Handlers Monitor ---

  const handleFilesDrop = useCallback((droppedFiles) => {
    // Se Totem mode e image, accetta solo 1 file
    if (mediaMode === 'totem' && totemType === 'image') {
        setFiles([droppedFiles[0]]);
    } else {
        setFiles(prev => [...prev, ...Array.from(droppedFiles)]);
    }
  }, [mediaMode, totemType]);

  const handlePdfSelected = (droppedFiles) => {
    if (droppedFiles.length > 0) {
      setPdfFile(droppedFiles[0]);
      setStep('pdfOptions');
    }
  };

  const handlePdfExtract = async (extractType) => {
    setIsLoading(true); setError(null); setLoadingMessage("Elaborazione PDF...");
    try {
      const response = await mediaApi.extractFromPdf(extractType, pdfFile);
      const base64Images = response.data.images || [];
      const imageObjects = base64Images.map((b64, index) => {
        const byteString = atob(b64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) { ia[i] = byteString.charCodeAt(i); }
        const blob = new Blob([ab], { type: 'image/png' });
        const file = new File([blob], `extracted_image_${index + 1}.png`, { type: 'image/png' });
        return { id: `extracted_${index}`, url: URL.createObjectURL(file), file: file };
      });
      setExtractedImages(imageObjects);
      setStep('pdfPreview');
    } catch (err) {
      console.error(err);
      setError("Errore durante l'estrazione PDF.");
      setStep('pdfOptions');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Handlers Totem ---
  
  const handleTotemConfigChange = (field, value) => {
    setTotemConfig(prev => ({ ...prev, [field]: value }));
  };

  // --- Final Save ---

  const handleFinalUpload = async () => {
    let filesToUpload = [];
    let uploadMetadata = {
        content_set_id: selectedContentSet?.id,
        media_mode: mediaMode
    };

    if (mediaMode === 'monitor') {
        // Logica Monitor
        filesToUpload = (step === 'imageUpload' || step === 'videoUpload')
            ? files
            : extractedImages.filter(img => selectedImages.includes(img.id)).map(img => img.file);
        
        if (filesToUpload.length === 0) { setError("Nessun file selezionato."); return; }
        if (filesToUpload.length > 50) { setError("Massimo 50 file alla volta."); return; }

    } else {
        // Logica Totem
        uploadMetadata = {
            ...uploadMetadata,
            totem_config: {
                ...totemConfig,
                // Converto array in stringhe CSV se il backend lo richiede così, o lascio array
                target_counters: totemConfig.counters.join(','),
                target_articles: totemConfig.articles.join(',')
            },
            type: totemType
        };

        if (totemType === 'image') {
            if (files.length === 0) { setError("Seleziona un'immagine."); return; }
            filesToUpload = files;
        } else if (totemType === 'text') {
            if (!totemText.trim()) { setError("Inserisci il testo."); return; }
            // Creo un file .txt al volo per mantenere la coerenza con l'uploader
            const blob = new Blob([totemText], { type: 'text/plain' });
            const file = new File([blob], "totem_message.txt", { type: 'text/plain' });
            filesToUpload = [file];
            uploadMetadata['totem_config']['value'] = totemText;
        }
    }

    await onSave(filesToUpload, uploadMetadata);
  };

  // --- Render Steps ---

  const renderContentSetStep = () => (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" textAlign="center">1. Seleziona un Content Set</Typography>
      {showNewContentSet ? (
        <Stack direction="row" spacing={1}>
          <TextField fullWidth label="Nome nuovo Set" value={newContentSetName} onChange={(e) => setNewContentSetName(e.target.value)} autoFocus />
          <Button variant="contained" onClick={handleCreateContentSet} disabled={!newContentSetName.trim()}>Salva</Button>
          <Button onClick={() => setShowNewContentSet(false)}>Annulla</Button>
        </Stack>
      ) : (
        <Autocomplete
          options={contentSets} getOptionLabel={(option) => option.content_set_name || ""} value={selectedContentSet}
          onChange={(event, newValue) => setSelectedContentSet(newValue)}
          renderInput={(params) => <TextField {...params} label="Cerca content set" />}
        />
      )}
      {!showNewContentSet && (
        <Button startIcon={<AddCircleOutlineIcon />} onClick={() => setShowNewContentSet(true)} sx={{ alignSelf: 'center' }}>Nuovo Content Set</Button>
      )}
      <Button 
        variant="contained" 
        onClick={() => setStep(mediaMode === 'monitor' ? 'selection' : 'totemSelection')} 
        disabled={!selectedContentSet} 
        sx={{ mt: 2, alignSelf: 'flex-end' }}
      >
        Continua
      </Button>
    </Box>
  );

  const renderSelectionStep = () => (
    <Stack spacing={3} sx={{ p: 2, pt: 1 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('contentSet')} sx={{ alignSelf: 'flex-start' }}>Indietro</Button>
      <Typography variant="h6" textAlign="center">2. Scegli tipo upload (Monitor)</Typography>
      <Grid container spacing={2}>
          <Grid item size={{xs:4}}>
            <Paper onClick={() => setStep('imageUpload')} elevation={2} sx={selectionCardStyle}>
                <PhotoLibraryIcon color="primary" sx={{ fontSize: 48 }} />
                <Typography variant="h6" mt={1}>Immagini</Typography>
            </Paper>
          </Grid>
          <Grid item size={{xs:4}}>
            <Paper onClick={() => setStep('videoUpload')} elevation={2} sx={selectionCardStyle}>
                <VideoLibraryOutlined color="primary" sx={{ fontSize: 48 }} />
                <Typography variant="h6" mt={1}>Video</Typography>
            </Paper>
          </Grid>
          <Grid item size={{xs:4}}>
            <Paper onClick={() => setStep('pdfUpload')} elevation={2} sx={selectionCardStyle}>
                <PictureAsPdfIcon color="error" sx={{ fontSize: 48 }} />
                <Typography variant="h6" mt={1}>PDF</Typography>
            </Paper>
          </Grid>
      </Grid>
    </Stack>
  );

  const renderTotemSelectionStep = () => (
    <Stack spacing={3} sx={{ p: 2, pt: 1 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('contentSet')} sx={{ alignSelf: 'flex-start' }}>Indietro</Button>
      <Typography variant="h6" textAlign="center">2. Scegli il formato del contenuto (Totem)</Typography>
      <Grid container spacing={3} justifyContent="center">
          <Grid item size={{xs:6}}>
            <Paper onClick={() => { setTotemType('image'); setStep('totemInput'); }} elevation={3} sx={selectionCardStyle}>
                <PhotoLibraryIcon color="primary" sx={{ fontSize: 60 }} />
                <Typography variant="h5" mt={2}>Immagine</Typography>
                <Typography variant="body2" color="text.secondary">Carica un'immagine pubblicitaria</Typography>
            </Paper>
          </Grid>
          <Grid item size={{xs:6}}>
             <Paper onClick={() => { setTotemType('text'); setStep('totemInput'); }} elevation={3} sx={selectionCardStyle}>
                <TextFieldsIcon color="secondary" sx={{ fontSize: 60 }} />
                <Typography variant="h5" mt={2}>Testo</Typography>
                <Typography variant="body2" color="text.secondary">Scrivi un messaggio personalizzato</Typography>
            </Paper>
          </Grid>
      </Grid>
    </Stack>
  );

  const renderTotemInputStep = () => (
      <Box sx={{ p: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => { setStep('totemSelection'); setFiles([]); setTotemText(""); }} sx={{ mb: 2 }}>Indietro</Button>
          <Typography variant="h6" gutterBottom textAlign="center">
              3. {totemType === 'image' ? "Carica l'immagine" : "Inserisci il testo"}
          </Typography>
          
          <Box sx={{ mt: 3, minHeight: 200 }}>
            {totemType === 'image' ? (
                <>
                    <Dropzone 
                        onDrop={handleFilesDrop} 
                        accept="image/jpg,image/jpeg,image/png" 
                        inputRef={inputRef}
                        maxFiles={1} 
                    />
                     {files.length > 0 && (
                        <Alert severity="success" sx={{ mt: 2 }} action={<IconButton size="small" onClick={() => setFiles([])}><DeleteIcon fontSize="inherit" /></IconButton>}>
                            File selezionato: <strong>{files[0].name}</strong>
                        </Alert>
                    )}
                </>
            ) : (
                <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Testo del Ticket"
                    variant="outlined"
                    value={totemText}
                    onChange={(e) => setTotemText(e.target.value)}
                    placeholder="Scrivi qui il messaggio che apparirà sul ticket..."
                    sx={{ backgroundColor: 'white' }}
                />
            )}
          </Box>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
             <Button 
                variant="contained" 
                size="large"
                endIcon={<SettingsIcon />}
                disabled={(totemType === 'image' && files.length === 0) || (totemType === 'text' && !totemText.trim())}
                onClick={() => setStep('totemConfig')}
             >
                 Configura Regole Stampa
             </Button>
          </Box>
      </Box>
  );

  const renderTotemConfigStep = () => (
      <Box sx={{ p: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('totemInput')} sx={{ mb: 2 }}>Indietro</Button>
        <Typography variant="h6" gutterBottom textAlign="center">4. Configurazione Regole di Stampa</Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            Definisci quando e dove questo contenuto deve essere stampato.
        </Typography>

        <Grid container spacing={3}>
            {/* 1. Trigger */}
            <Grid item size={{xs:12}}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Quando stampare?</Typography>
                    <ToggleButtonGroup
                        value={totemConfig.trigger}
                        exclusive
                        onChange={(e, val) => val && handleTotemConfigChange('trigger', val)}
                        fullWidth
                        color="primary"
                    >
                        <ToggleButton value={1}>
                            <TicketIcon sx={{ mr: 1 }} /> Prenotazione Ticket
                        </ToggleButton>
                        <ToggleButton value={2}>
                            <RestaurantIcon sx={{ mr: 1 }} /> Stampa Ingredienti
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Paper>
            </Grid>

            {/* 2 & 3. Counters & Articles */}
            <Grid item size={{xs:12, md:4}}>
                 <TagInput 
                    label="Banchi Abilitati (Opzionale)" 
                    placeholder="Codice Banco e Invio"
                    value={totemConfig.counters} 
                    onChange={(val) => handleTotemConfigChange('counters', val)} 
                 />
            </Grid>
            <Grid item size={{xs:12, md:4}}>
                 <TagInput 
                    label="Codici Articolo (Opzionale)" 
                    placeholder="Codice Articolo e Invio"
                    value={totemConfig.articles} 
                    onChange={(val) => handleTotemConfigChange('articles', val)}
                    maxLength={13}
                 />
            </Grid>

            <Grid item size={{xs:12}}><Divider /></Grid>

            {/* 4 & 5. Priority & Queue */}
            <Grid item size={{xs:6}}>
                <TextField 
                    fullWidth 
                    type="number" 
                    label="Priorità di Stampa" 
                    value={totemConfig.priority}
                    onChange={(e) => handleTotemConfigChange('priority', e.target.value)}
                    helperText="1 = Massima priorità"
                    InputProps={{ inputProps: { min: 1 } }}
                />
            </Grid>
            <Grid item size={{xs:6}}>
                <TextField 
                    fullWidth 
                    type="number" 
                    label="Soglia Coda Minima" 
                    value={totemConfig.queueThreshold}
                    onChange={(e) => handleTotemConfigChange('queueThreshold', e.target.value)}
                    helperText="Stampa solo se ci sono più di X persone"
                    InputProps={{ inputProps: { min: 0 } }}
                />
            </Grid>
        </Grid>
      </Box>
  );

  // Stili comuni
  const selectionCardStyle = {
      p: 4, textAlign: 'center', cursor: 'pointer', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { backgroundColor: 'action.hover', transform: 'translateY(-4px)', boxShadow: 4 }
  };

  const renderCurrentStep = () => {
      switch (step) {
          case 'contentSet': return renderContentSetStep();
          
          // Monitor Flow
          case 'selection': return renderSelectionStep();
          case 'imageUpload':
          case 'videoUpload':
          case 'pdfUpload':
              return (
                  <Box sx={{ p: 1 }}>
                      <Button startIcon={<ArrowBackIcon />} onClick={() => { setStep('selection'); setFiles([]); }} sx={{ mb: 2 }}>Indietro</Button>
                      <Dropzone 
                        onDrop={step === 'pdfUpload' ? handlePdfSelected : handleFilesDrop}
                        accept={step === 'pdfUpload' ? 'application/pdf' : step === 'imageUpload' ? 'image/jpg,image/jpeg,image/png' : 'video/mp4'}
                        isPdf={step === 'pdfUpload'}
                        inputRef={inputRef}
                      />
                       {/* Lista file per Monitor (multiplo) */}
                       {files.length > 0 && step !== 'pdfUpload' && (
                          <Box mt={2}>
                              <Typography variant="subtitle1">File Selezionati:</Typography>
                              <List dense>
                                  {files.map((file, i) => (
                                      <ListItem key={i} secondaryAction={<IconButton edge="end" onClick={() => setFiles(p => p.filter(f => f !== file))}><DeleteIcon /></IconButton>}>
                                          <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(0)} KB`} />
                                      </ListItem>
                                  ))}
                              </List>
                          </Box>
                      )}
                  </Box>
              );
          case 'pdfOptions': // ... codice pdfOptions invariato, o semplificato per brevità qui
                return (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('pdfUpload')} sx={{ mb: 2, alignSelf: 'flex-start' }}>Indietro</Button>
                        <Typography variant="h6">File: {pdfFile?.name}</Typography>
                        <Stack spacing={2} mt={3}>
                        <Button variant="contained" startIcon={<ImageSearchIcon />} onClick={() => handlePdfExtract('perImage')}>Estrai Immagini</Button>
                        <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => handlePdfExtract('perPage')}>Converti Pagine</Button>
                        </Stack>
                    </Box>
                );
          case 'pdfPreview': // ... codice pdfPreview simile a prima
                return (
                    <Box sx={{ p: 1 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => { setStep('pdfOptions'); setExtractedImages([]); }} sx={{ mb: 2 }}>Indietro</Button>
                        <Grid container spacing={2} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                        {extractedImages.map(image => (
                            <Grid item size={{xs:6, sm:4}} key={image.id}>
                                <Paper variant="outlined" sx={{ position: 'relative' }}>
                                    <FormControlLabel control={<Checkbox checked={selectedImages.includes(image.id)} onChange={() => setSelectedImages(p => p.includes(image.id) ? p.filter(x => x !== image.id) : [...p, image.id])} />} label="" sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, width: '100%', height: '100%', m: 0 }} />
                                    <Box component="img" src={image.url} sx={{ width: '100%', height: 150, objectFit: 'cover' }} />
                                </Paper>
                            </Grid>
                        ))}
                        </Grid>
                    </Box>
                );

          // Totem Flow
          case 'totemSelection': return renderTotemSelectionStep();
          case 'totemInput': return renderTotemInputStep();
          case 'totemConfig': return renderTotemConfigStep();

          default: return null;
      }
  };

  // Determina se mostrare il pulsante "Carica" nella barra delle azioni
  const canShowSave = () => {
      if (mediaMode === 'monitor') {
          return (step === 'imageUpload' && files.length > 0) || 
                 (step === 'videoUpload' && files.length > 0) || 
                 (step === 'pdfPreview' && selectedImages.length > 0);
      } else {
          // Totem: il pulsante carica appare solo alla fine
          return step === 'totemConfig';
      }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {mediaMode === 'monitor' ? 'Carica Contenuti Monitor' : 'Configura Ticket Totem'}
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: '450px', backgroundColor: 'grey.50', position: 'relative' }}>
          {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10 }}>
              <WaitingDialog open={true} message={loadingMessage} />
            </Box>
          ) : renderCurrentStep()}
        </DialogContent>
        {canShowSave() && (
          <DialogActions sx={{ p: '16px 24px', backgroundColor: 'grey.100' }}>
            <Typography sx={{ flexGrow: 1 }} color="text.secondary">
               {mediaMode === 'monitor' && step !== 'pdfPreview' ? `${files.length} file pronti` : ''}
            </Typography>
            <Button onClick={handleClose} color="inherit">Annulla</Button>
            <Button 
                variant="contained" 
                onClick={() => handleFinalUpload().then(() => handleClose())} 
                startIcon={<CloudUploadIcon />}
                size="large"
            >
                {mediaMode === 'totem' ? 'Salva Configurazione' : 'Carica File'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}